import React, { useCallback, useMemo, useRef, useState } from "react";
import { read, utils } from "xlsx";
import { toast } from "react-toastify";
import { convertInputToUTC } from "../../util/date";
import AddGamesBulkMutation from "./mutation/BulkAddGames";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const EMPTY_LIST = [];

const makeRowKey = () =>
    `import_row_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;

const normalize = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

const normalizeSoft = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

const headerMatchesAlias = (rawHeader, alias) => {
    const header = normalize(rawHeader);
    return header === alias || header.startsWith(alias);
};

const toDateTimeLocal = (value) => {
    if (value == null || value === "") return "";

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
            value.getDate()
        )}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
    }

    if (typeof value === "number") {
        const parsed = utils.parse_date_code(value);
        if (!parsed) return "";

        const d = new Date(
            parsed.y,
            (parsed.m || 1) - 1,
            parsed.d || 1,
            parsed.H || 0,
            parsed.M || 0,
            Math.round(parsed.S || 0)
        );

        return toDateTimeLocal(d);
    }

    const asText = String(value).trim();
    if (!asText) return "";

    const spacedDateTime = asText.match(
        /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/
    );

    if (spacedDateTime) {
        const [, y, m, d, h, min] = spacedDateTime;
        return `${y}-${m}-${d}T${h}:${min}`;
    }

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(asText)) {
        return asText.slice(0, 16);
    }

    const parsedDate = new Date(asText);
    if (!Number.isNaN(parsedDate.getTime())) {
        return toDateTimeLocal(parsedDate);
    }

    return "";
};

const getTeamLabel = (team) => team?.teamName || team?.name || "";

const getFieldLabel = (field) => {
    const fieldName = field?.name || "";

    const facilityName =
        field?.sportFacility?.name ||
        field?.sport_facility?.name ||
        field?.facilityName ||
        field?.facility?.name ||
        field?.sportFacilityName ||
        "";

    if (facilityName && fieldName) return `${facilityName} - ${fieldName}`;
    return fieldName;
};

const getProfileLabel = (profile) =>
    profile?.fullName ||
    profile?.full_name ||
    `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim();

const findByNameLoose = (list, getLabel, value) => {
    const target = normalize(value);
    const softTarget = normalizeSoft(value);

    if (!target) return null;

    const exact =
        (list || []).find((item) => normalize(getLabel(item)) === target) || null;

    if (exact) return exact;

    const softExact =
        (list || []).find(
            (item) => normalizeSoft(getLabel(item)) === softTarget
        ) || null;

    if (softExact) return softExact;

    return null;
};

const findFieldByName = (fields, value) => {
    if (!value) return null;

    return (
        findByNameLoose(fields, getFieldLabel, value) ||
        findByNameLoose(fields, (field) => field?.name, value) ||
        findByNameLoose(fields, (field) => String(field), value)
    );
};

const emptyImportRow = (stages = []) => {
    const firstStage = stages?.[0] || null;
    const firstGroup = firstStage?.groups?.[0] || null;

    return {
        _rowKey: makeRowKey(),
        _sourceRowNumber: null,
        _importIssues: [],
        stageId: firstStage?.id ? String(firstStage.id) : "",
        groupId: firstGroup?.id ? String(firstGroup.id) : "",
        description: "",
        dateTime: "",
        homeTeamId: "",
        visitorTeamId: "",
        fieldId: "",
        refereeIds: [],
        managerIds: [],
        matchDay: "",
        groups: firstStage?.groups || [],
        teams: firstGroup?.teams || firstStage?.teams || [],
        fields: firstStage?.fields || [],
    };
};

const isRowBlank = (row) => {
    if (!row) return true;

    return ![
        row.dateTime,
        row.homeTeamId,
        row.visitorTeamId,
        row.fieldId,
        row.matchDay,
        row.description,
        ...(row.refereeIds || []),
        ...(row.managerIds || []),
        row.groupId,
    ].some((v) => String(v || "").trim() !== "");
};

const hasMeaningfulSheetData = (rawRow, headerIndex) => {
    if (!rawRow) return false;

    const meaningfulKeys = [
        "dateTime",
        "visitorTeam",
        "homeTeam",
        "field",
        "referee",
        "manager",
        "matchDay",
        "description",
        "group",
    ];

    return meaningfulKeys.some((key) => {
        const idx = headerIndex[key];
        if (idx == null) return false;
        return String(rawRow[idx] ?? "").trim() !== "";
    });
};

const headerAliases = {
    stage: ["stage", "stagename", "phase"],
    group: ["group", "groupname"],
    dateTime: ["datetime", "date", "dateandtime", "gamedatetime"],
    visitorTeam: ["away", "awayteam", "visitor", "visitorteam"],
    homeTeam: ["home", "hometeam", "local", "localteam"],
    field: ["field", "venue", "location", "fieldname"],
    referee: ["referee", "umpire", "responsible", "ref", "ump", "refump"],
    manager: ["manager", "coordinator"],
    matchDay: ["matchday", "matchweek", "jornada"],
    description: ["description", "notes", "comment"],
};

export default function ImportGamesExcelModal({
                                                  modalId = "import-games-excel-modal",
                                                  stages = [],
                                                  referees = [],
                                                  managers = [],
                                                  onImported,
                                              }) {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const safeStages = Array.isArray(stages) ? stages : EMPTY_LIST;
    const safeReferees = Array.isArray(referees) ? referees : EMPTY_LIST;
    const safeManagers = Array.isArray(managers) ? managers : EMPTY_LIST;

    const [addGamesBulk] = AddGamesBulkMutation();

    const [rows, setRows] = useState(() => [emptyImportRow(safeStages)]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [parsing, setParsing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fileName, setFileName] = useState("");
    const [detectedTemplateType, setDetectedTemplateType] = useState("");

    const fileInputRef = useRef(null);

    const scopeSel = useMemo(() => `#${modalId}`, [modalId]);
    const fileInputId = useMemo(() => `${modalId}-excel-file-input`, [modalId]);

    const getDisplayRowNumber = useCallback((row, index) => {
        return row._sourceRowNumber || index + 1;
    }, []);

    const resolveRowDependencies = useCallback((base, allStages) => {
        const selectedStage =
            (allStages || []).find(
                (stage) => String(stage.id) === String(base.stageId)
            ) || null;

        const groups = selectedStage?.groups || [];

        let groupId = base.groupId || "";
        if (
            groupId &&
            groups.length &&
            !groups.some((group) => String(group.id) === String(groupId))
        ) {
            groupId = "";
        }

        const selectedGroup =
            groups.find((group) => String(group.id) === String(groupId)) || null;

        const teams = selectedGroup?.teams || selectedStage?.teams || [];
        const fields = selectedStage?.fields || [];

        const homeTeamId = teams.some(
            (team) => String(team.teamId ?? team.id) === String(base.homeTeamId)
        )
            ? String(base.homeTeamId)
            : "";

        const visitorTeamId = teams.some(
            (team) => String(team.teamId ?? team.id) === String(base.visitorTeamId)
        )
            ? String(base.visitorTeamId)
            : "";

        const fieldId = fields.some(
            (field) => String(field.id) === String(base.fieldId)
        )
            ? String(base.fieldId)
            : "";

        return {
            ...base,
            stageId: selectedStage ? String(base.stageId) : "",
            groupId,
            groups,
            teams,
            fields,
            homeTeamId,
            visitorTeamId,
            fieldId,
        };
    }, []);

    const validateRowForErrors = useCallback(
        (row, displayRow) => {
            const errors = [];

            if (
                !row.stageId ||
                !row.dateTime ||
                !row.homeTeamId ||
                !row.visitorTeamId ||
                !row.fieldId ||
                !row.matchDay
            ) {
                errors.push({
                    row: displayRow,
                    rowKey: row._rowKey,
                    error: t("fixtures.importModal.errors.completeAllRequired"),
                });
            }

            if (row.homeTeamId && row.homeTeamId === row.visitorTeamId) {
                errors.push({
                    row: displayRow,
                    rowKey: row._rowKey,
                    error: t("fixtures.importModal.errors.teamsMustBeDifferent"),
                });
            }

            const matchDayNumber = Number(row.matchDay);
            if (
                row.matchDay &&
                (!Number.isFinite(matchDayNumber) || matchDayNumber <= 0)
            ) {
                errors.push({
                    row: displayRow,
                    rowKey: row._rowKey,
                    error: t("fixtures.importModal.errors.invalidMatchDay"),
                });
            }

            return errors;
        },
        [t]
    );

    const updateRowField = useCallback(
        (rowKey, key, value) => {
            setRows((prevRows) => {
                let editedRow = null;

                const nextRows = prevRows.map((row) => {
                    if (row._rowKey !== rowKey) return row;

                    const next = {
                        ...row,
                        [key]: value,
                    };

                    if (key === "stageId") {
                        next.refereeIds = [];
                        next.managerIds = [];
                    }

                    editedRow =
                        key === "stageId" || key === "groupId"
                            ? resolveRowDependencies(next, safeStages)
                            : next;

                    return editedRow;
                });

                if (editedRow) {
                    const displayRow =
                        editedRow._sourceRowNumber ||
                        nextRows.findIndex((row) => row._rowKey === rowKey) + 1;

                    const rowValidationErrors = validateRowForErrors(
                        editedRow,
                        displayRow
                    );

                    setValidationErrors((prevErrors) => {
                        const errorsFromOtherRows = prevErrors.filter(
                            (err) => err?.rowKey !== rowKey
                        );

                        return [...errorsFromOtherRows, ...rowValidationErrors];
                    });

                    if (!rowValidationErrors.length) {
                        return nextRows.map((row) =>
                            row._rowKey === rowKey
                                ? {
                                    ...row,
                                    _importIssues: [],
                                }
                                : row
                        );
                    }
                }

                return nextRows;
            });
        },
        [resolveRowDependencies, safeStages, validateRowForErrors]
    );

    const removeRow = useCallback((rowKey) => {
        setRows((prev) => prev.filter((row) => row._rowKey !== rowKey));
        setValidationErrors((prev) =>
            prev.filter((err) => err?.rowKey !== rowKey)
        );
    }, []);

    const validateRows = useCallback(() => {
        const errors = [];
        const rowsWithData = rows.filter((row) => !isRowBlank(row));

        rowsWithData.forEach((row, idx) => {
            const displayRow = row._sourceRowNumber || idx + 1;

            if (row._importIssues?.length) {
                row._importIssues.forEach((issue) => {
                    errors.push({
                        row: displayRow,
                        rowKey: row._rowKey,
                        error: issue,
                    });
                });
            }

            const rowValidationErrors = validateRowForErrors(row, displayRow);
            errors.push(...rowValidationErrors);
        });

        return errors;
    }, [rows, validateRowForErrors]);

    const mapSheetRows = useCallback(
        (sheetRows) => {
            if (!sheetRows?.length) return [];

            const headerRow = sheetRows[0] || [];
            const headerIndex = {};

            Object.keys(headerAliases).forEach((key) => {
                const aliasSet = headerAliases[key];

                const idx = headerRow.findIndex((header) =>
                    aliasSet.some((alias) => headerMatchesAlias(header, alias))
                );

                if (idx >= 0) headerIndex[key] = idx;
            });

            const required = [
                "stage",
                "dateTime",
                "visitorTeam",
                "homeTeam",
                "field",
                "matchDay",
            ];

            const missing = required.filter((key) => headerIndex[key] == null);

            if (missing.length) {
                throw new Error(
                    t("fixtures.importModal.errors.missingColumns", {
                        columns: missing.join(", "),
                    })
                );
            }

            const hasHiddenIdColumns = headerRow.some((header) =>
                [
                    "groupid",
                    "awayteamid",
                    "hometeamid",
                    "fieldid",
                    "refereeid",
                    "coordinatorid",
                    "stageid",
                ].includes(normalize(header))
            );

            setDetectedTemplateType(
                hasHiddenIdColumns
                    ? t("fixtures.importModal.dropdownImportType")
                    : t("fixtures.importModal.blankImportType")
            );

            const parsedRows = [];

            for (let i = 1; i < sheetRows.length; i += 1) {
                const rawRow = sheetRows[i] || [];

                if (!hasMeaningfulSheetData(rawRow, headerIndex)) {
                    continue;
                }

                const sourceRowNumber = i + 1;

                const stageName = rawRow[headerIndex.stage];
                const groupName =
                    headerIndex.group != null ? rawRow[headerIndex.group] : "";

                const visitorName = rawRow[headerIndex.visitorTeam];
                const homeName = rawRow[headerIndex.homeTeam];
                const fieldName = rawRow[headerIndex.field];

                const dateTimeRaw = rawRow[headerIndex.dateTime];
                const matchDayRaw = rawRow[headerIndex.matchDay];

                const descriptionRaw =
                    headerIndex.description != null
                        ? rawRow[headerIndex.description]
                        : "";

                const importIssues = [];

                const stage = findByNameLoose(safeStages, (stage) => stage?.name, stageName);

                if (!stage) {
                    importIssues.push(
                        t("fixtures.importModal.errors.stageNotFound", {
                            value: String(stageName || ""),
                        })
                    );
                }

                const stageGroups = stage?.groups || [];

                const group = groupName
                    ? findByNameLoose(stageGroups, (group) => group?.name, groupName)
                    : null;

                if (groupName && stage && !group) {
                    importIssues.push(
                        t("fixtures.importModal.errors.groupNotFound", {
                            value: String(groupName || ""),
                        })
                    );
                }

                const teams = group?.teams || stage?.teams || [];
                const fields = stage?.fields || [];

                const visitorTeam = findByNameLoose(teams, getTeamLabel, visitorName);
                const homeTeam = findByNameLoose(teams, getTeamLabel, homeName);
                const field = findFieldByName(fields, fieldName);

                const refereeNames =
                    headerIndex.referee != null ? String(rawRow[headerIndex.referee] || "").split(",").map(s => s.trim()).filter(Boolean) : [];

                const managerNames =
                    headerIndex.manager != null ? String(rawRow[headerIndex.manager] || "").split(",").map(s => s.trim()).filter(Boolean) : [];

                const refereeIds = refereeNames.map(name => {
                    const ref = findByNameLoose(safeReferees, getProfileLabel, name);
                    return ref ? String(ref.id) : null;
                }).filter(Boolean);

                const managerIds = managerNames.map(name => {
                    const mgr = findByNameLoose(safeManagers, getProfileLabel, name);
                    return mgr ? String(mgr.id) : null;
                }).filter(Boolean);

                refereeNames.forEach((name, idx) => {
                    if (name && !refereeIds[idx]) {
                        importIssues.push(
                            t("fixtures.importModal.errors.refereeNotFound", {
                                value: String(name || ""),
                            })
                        );
                    }
                });

                managerNames.forEach((name, idx) => {
                    if (name && !managerIds[idx]) {
                        importIssues.push(
                            t("fixtures.importModal.errors.managerNotFound", {
                                value: String(name || ""),
                            })
                        );
                    }
                });

                const localDateTime = toDateTimeLocal(dateTimeRaw);
                const matchDayValue = String(matchDayRaw ?? "").trim();

                if (visitorName && stage && !visitorTeam) {
                    importIssues.push(
                        t("fixtures.importModal.errors.awayTeamNotFound", {
                            value: String(visitorName || ""),
                        })
                    );
                }

                if (homeName && stage && !homeTeam) {
                    importIssues.push(
                        t("fixtures.importModal.errors.homeTeamNotFound", {
                            value: String(homeName || ""),
                        })
                    );
                }

                if (fieldName && stage && !field) {
                    importIssues.push(
                        t("fixtures.importModal.errors.fieldNotFound", {
                            value: String(fieldName || ""),
                        })
                    );
                }

                if (dateTimeRaw && !localDateTime) {
                    importIssues.push(t("fixtures.importModal.errors.invalidDateTime"));
                }

                parsedRows.push(
                    resolveRowDependencies(
                        {
                            _rowKey: makeRowKey(),
                            _sourceRowNumber: sourceRowNumber,
                            _importIssues: importIssues,
                            stageId: stage?.id ? String(stage.id) : "",
                            groupId: group?.id ? String(group.id) : "",
                            description: String(descriptionRaw || ""),
                            dateTime: localDateTime,
                            homeTeamId: homeTeam
                                ? String(homeTeam.teamId ?? homeTeam.id)
                                : "",
                            visitorTeamId: visitorTeam
                                ? String(visitorTeam.teamId ?? visitorTeam.id)
                                : "",
                            fieldId: field?.id ? String(field.id) : "",
                            refereeIds,
                            managerIds,
                            matchDay: matchDayValue,
                            groups: stageGroups,
                            teams,
                            fields,
                        },
                        safeStages
                    )
                );
            }

            return parsedRows;
        },
        [safeManagers, safeReferees, resolveRowDependencies, safeStages, t]
    );

    const handleFileChange = useCallback(
        async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (!/\.(xlsx|xls)$/i.test(file.name)) {
                toast.error(t("fixtures.importModal.errors.onlyExcelFiles"), {
                    autoClose: 3000,
                    toastId: "import_games_bad_file_type",
                    position: "top-center",
                });

                e.target.value = "";
                return;
            }

            setFileName(file.name);
            setParsing(true);
            setValidationErrors([]);
            setDetectedTemplateType("");

            try {
                const buffer = await file.arrayBuffer();

                const workbook = read(buffer, {
                    type: "array",
                    cellDates: true,
                });

                const firstSheetName = workbook.SheetNames?.[0];
                if (!firstSheetName) {
                    throw new Error(t("fixtures.importModal.errors.emptyWorkbook"));
                }

                const sheet = workbook.Sheets[firstSheetName];

                const sheetRows = utils.sheet_to_json(sheet, {
                    header: 1,
                    defval: "",
                    raw: true,
                });

                const mapped = mapSheetRows(sheetRows);

                if (!mapped.length) {
                    throw new Error(t("fixtures.importModal.errors.noRowsDetected"));
                }

                setRows(mapped);

                const rowErrors = mapped
                    .filter((row) => row._importIssues?.length)
                    .flatMap((row, idx) =>
                        row._importIssues.map((issue) => ({
                            row: row._sourceRowNumber || idx + 1,
                            rowKey: row._rowKey,
                            error: issue,
                        }))
                    );

                if (rowErrors.length) {
                    setValidationErrors(rowErrors);

                    toast.warning(
                        t("fixtures.importModal.messages.fileParsedWithErrors", {
                            count: mapped.length,
                            errors: rowErrors.length,
                        }),
                        {
                            autoClose: 3500,
                            toastId: "import_games_rows_parsed_with_errors",
                            position: "top-center",
                        }
                    );
                } else {
                    toast.success(
                        t("fixtures.importModal.messages.fileParsed", {
                            count: mapped.length,
                        }),
                        {
                            autoClose: 2500,
                            toastId: "import_games_rows_parsed",
                            position: "top-center",
                        }
                    );
                }
            } catch (err) {
                toast.error(
                    getLocalizedGraphQLError(err, t, "fixtures.importModal.errors.parseFailed"),
                    {
                        autoClose: 3500,
                        toastId: "import_games_parse_error",
                        position: "top-center",
                    }
                );

                setRows([emptyImportRow(safeStages)]);
            } finally {
                setParsing(false);
            }
        },
        [mapSheetRows, safeStages, t]
    );

    const resetImport = useCallback(() => {
        setRows([emptyImportRow(safeStages)]);
        setValidationErrors([]);
        setFileName("");
        setDetectedTemplateType("");

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, [safeStages]);

    const closeModal = useCallback(() => {
        if (window.$ && typeof window.$(scopeSel)?.modal === "function") {
            window.$(scopeSel).modal("hide");
        }
    }, [scopeSel]);

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();

            const rowsWithData = rows.filter((row) => !isRowBlank(row));

            if (!rowsWithData.length) {
                toast.error(t("fixtures.importModal.errors.noRowsDetected"), {
                    autoClose: 3000,
                    toastId: "import_games_no_data_rows",
                    position: "top-center",
                });

                return;
            }

            const errors = validateRows();

            if (errors.length) {
                setValidationErrors(errors);
                return;
            }

            setSaving(true);

            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            try {
                const games = rowsWithData.map((row) => ({
                    stageId: row.stageId,
                    groupId: row.groupId || null,
                    description: row.description || "",
                    fieldId: row.fieldId,
                    homeTeamId: row.homeTeamId,
                    visitorTeamId: row.visitorTeamId,
                    dateTime: convertInputToUTC(row.dateTime, timezone),
                    timezone,
                    matchDay: row.matchDay,
                    refereeIds: row.refereeIds || [],
                    managerIds: row.managerIds || [],
                }));

                const result = await addGamesBulk({
                    variables: {
                        games,
                    },
                });

                if (result.data?.addGamesBulk?.success) {
                    toast.success(t("fixtures.importModal.messages.importSuccess"), {
                        autoClose: 2500,
                        toastId: "import_games_success",
                        position: "top-center",
                    });

                    resetImport();
                    closeModal();
                    onImported?.();
                } else {
                    let errs = result.data?.addGamesBulk?.errors || [];

                    if (errs.length && typeof errs[0] === "string") {
                        errs = errs.map((errStr) => {
                            try {
                                return JSON.parse(errStr);
                            } catch {
                                return {
                                    row: "-",
                                    error: errStr,
                                };
                            }
                        });
                    }

                    setValidationErrors(errs);
                }
            } catch (err) {
                toast.error(
                    t("fixtures.importModal.errors.importFailed", {
                        message: getLocalizedGraphQLError(err, t, "fixtures.importModal.errors.importFailed"),
                    }),
                    {
                        autoClose: 3500,
                        toastId: "import_games_submit_error",
                        position: "top-center",
                    }
                );
            } finally {
                setSaving(false);
            }
        },
        [addGamesBulk, closeModal, onImported, resetImport, rows, t, validateRows]
    );

    return (
        <div
            className="modal fade kbola-importModal"
            id={modalId}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="importGamesExcelTitle"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content kbola-modalContent">
                    <form onSubmit={handleSubmit}>
                        <div className="modal-header kbola-modalHeader">
                            <div
                                className="d-flex align-items-center"
                                style={{ gap: 10, minWidth: 0 }}
                            >
                                <span className="kbola-pill kbola-pillBrand">
                                    {t("fixtures.importModal.badge")}
                                </span>

                                <h4
                                    className="modal-title mb-0 kbola-modalTitle"
                                    id="importGamesExcelTitle"
                                >
                                    {t("fixtures.importModal.title")}
                                </h4>
                            </div>

                            <button
                                type="button"
                                className="close kbola-closeBtn"
                                data-dismiss="modal"
                                aria-label={t("fixtures.importModal.close")}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        <div className="modal-body kbola-modalBody">
                            <div className="kbola-uploadCard mb-3">
                                <div className="kbola-uploadTop">
                                    <div className="kbola-uploadTitle">
                                        {t("fixtures.importModal.uploadTitle")}
                                    </div>

                                    <div className="kbola-uploadHint">
                                        {t("fixtures.importModal.uploadHint")}
                                    </div>
                                </div>

                                <input
                                    ref={fileInputRef}
                                    id={fileInputId}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    className="kbola-fileNative"
                                    disabled={parsing || saving}
                                />

                                <div
                                    className="kbola-fileShell"
                                    role="group"
                                    aria-label={t("fixtures.importModal.uploadTitle")}
                                >
                                    <label htmlFor={fileInputId} className="kbola-fileBtn mb-0">
                                        <i className="fas fa-upload mr-2" />
                                        {parsing
                                            ? t("fixtures.importModal.buttons.importing")
                                            : t("fixtures.importModal.uploadTitle")}
                                    </label>

                                    <div className="kbola-fileMeta">
                                        {fileName ? (
                                            <span className="kbola-fileName" title={fileName}>
                                                {fileName}
                                            </span>
                                        ) : (
                                            <span className="kbola-filePlaceholder">
                                                {t("fixtures.importModal.uploadHint")}
                                            </span>
                                        )}

                                        <div className="kbola-fileSubhint">
                                            {detectedTemplateType
                                                ? t("fixtures.importModal.detectedTemplateType", {
                                                    type: detectedTemplateType,
                                                })
                                                : t("fixtures.importModal.supportedTemplatesHint")}
                                        </div>
                                    </div>

                                    {fileName ? (
                                        <button
                                            type="button"
                                            className="kbola-fileClear"
                                            onClick={resetImport}
                                            disabled={parsing || saving}
                                            title={t("fixtures.importModal.clear")}
                                            aria-label={t("fixtures.importModal.clear")}
                                        >
                                            <i className="fas fa-times" />
                                        </button>
                                    ) : null}
                                </div>
                            </div>

                            {validationErrors?.length ? (
                                <div className="kbola-alertDanger">
                                    <div className="kbola-alertTitle">
                                        {t("fixtures.importModal.errors.fixRows")}
                                    </div>

                                    <ul className="kbola-alertList">
                                        {validationErrors.map((err, idx) => (
                                            <li key={`import_err_${idx}_${err?.rowKey || "x"}`}>
                                                <strong>
                                                    {t("fixtures.importModal.rowLabel", {
                                                        row: err?.row ?? "-",
                                                    })}
                                                </strong>{" "}
                                                {err?.error}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null}

                            <div className="table-responsive kbola-tableWrap">
                                <table className="table table-striped table-sm mb-0 kbola-table">
                                    <thead className="kbola-thead">
                                    <tr>
                                        <th className="kbola-rowNumberHeader">
                                            {t("fixtures.importModal.rowNumber") || "Row"}
                                        </th>
                                        <th>{t("bulkAddGamesModal.headers.stage")}</th>
                                        <th>{t("bulkAddGamesModal.headers.group")}</th>
                                        <th>{t("bulkAddGamesModal.headers.datetime")}</th>
                                        <th>{t("bulkAddGamesModal.headers.away")}</th>
                                        <th>{t("bulkAddGamesModal.headers.home")}</th>
                                        <th>{t("bulkAddGamesModal.headers.venue")}</th>
                                        <th>{t("bulkAddGamesModal.headers.responsible")}</th>
                                        <th>{t("bulkAddGamesModal.headers.manager")}</th>
                                        <th>{t("bulkAddGamesModal.headers.matchday")}</th>
                                        <th>{t("bulkAddGamesModal.headers.description")}</th>
                                        <th className="text-right" />
                                    </tr>
                                    </thead>

                                    <tbody className="kbola-tbody">
                                    {rows.map((row, index) => (
                                        <tr key={row._rowKey}>
                                            <td className="kbola-rowNumberCell">
                                                    <span className="kbola-rowNumberBadge">
                                                        {getDisplayRowNumber(row, index)}
                                                    </span>
                                            </td>

                                            <td>
                                                <select
                                                    className="form-control kbola-control"
                                                    value={row.stageId}
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "stageId",
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                >
                                                    <option value="">
                                                        {t("bulkAddGamesModal.choose")}
                                                    </option>

                                                    {safeStages.map((stage) => (
                                                        <option
                                                            key={`i_stage_${stage.id}`}
                                                            value={stage.id}
                                                        >
                                                            {stage.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>

                                            <td>
                                                {row.groups?.length ? (
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={row.groupId}
                                                        onChange={(e) =>
                                                            updateRowField(
                                                                row._rowKey,
                                                                "groupId",
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="">
                                                            {t("bulkAddGamesModal.choose")}
                                                        </option>

                                                        {row.groups.map((group) => (
                                                            <option
                                                                key={`i_group_${group.id}`}
                                                                value={group.id}
                                                            >
                                                                {group.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className="kbola-pill kbola-pillMuted">
                                                            {t("bulkAddGamesModal.notAvailable")}
                                                        </span>
                                                )}
                                            </td>

                                            <td>
                                                <input
                                                    type="datetime-local"
                                                    className="form-control kbola-control"
                                                    value={row.dateTime}
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "dateTime",
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                />
                                            </td>

                                            <td>
                                                <select
                                                    className="form-control kbola-control"
                                                    value={row.visitorTeamId}
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "visitorTeamId",
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                >
                                                    <option value="">
                                                        {t("bulkAddGamesModal.choose")}
                                                    </option>

                                                    {(row.teams || []).map((team) => {
                                                        const value = String(
                                                            team.teamId ?? team.id ?? ""
                                                        );

                                                        return (
                                                            <option key={`i_away_${value}`} value={value}>
                                                                {getTeamLabel(team)}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </td>

                                            <td>
                                                <select
                                                    className="form-control kbola-control"
                                                    value={row.homeTeamId}
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "homeTeamId",
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                >
                                                    <option value="">
                                                        {t("bulkAddGamesModal.choose")}
                                                    </option>

                                                    {(row.teams || []).map((team) => {
                                                        const value = String(
                                                            team.teamId ?? team.id ?? ""
                                                        );

                                                        return (
                                                            <option key={`i_home_${value}`} value={value}>
                                                                {getTeamLabel(team)}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </td>

                                            <td>
                                                <select
                                                    className="form-control kbola-control"
                                                    value={row.fieldId}
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "fieldId",
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                >
                                                    <option value="">
                                                        {t("bulkAddGamesModal.choose")}
                                                    </option>

                                                    {(row.fields || []).map((field) => (
                                                        <option key={`i_field_${field.id}`} value={field.id}>
                                                            {getFieldLabel(field)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>

                                            <td>
                                                <select
                                                    className="form-control kbola-control"
                                                    value={row.refereeIds}
                                                    multiple
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "refereeIds",
                                                            Array.from(e.target.selectedOptions).map(o => o.value)
                                                        )
                                                    }
                                                >
                                                    {safeReferees.map((profile) => (
                                                        <option key={`i_ref_${profile.id}`} value={profile.id}>
                                                            {getProfileLabel(profile)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>

                                            <td>
                                                <select
                                                    className="form-control kbola-control"
                                                    value={row.managerIds}
                                                    multiple
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "managerIds",
                                                            Array.from(e.target.selectedOptions).map(o => o.value)
                                                        )
                                                    }
                                                >

                                                    {safeManagers.map((profile) => (
                                                        <option key={`i_mgr_${profile.id}`} value={profile.id}>
                                                            {getProfileLabel(profile)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>

                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control kbola-control kbola-matchdayControl"
                                                    value={String(row.matchDay || "")}
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "matchDay",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder={t("bulkAddGamesModal.headers.matchday")}
                                                    required
                                                />
                                            </td>

                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control kbola-control kbola-descriptionControl"
                                                    value={row.description || ""}
                                                    onChange={(e) =>
                                                        updateRowField(
                                                            row._rowKey,
                                                            "description",
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder={t(
                                                        "bulkAddGamesModal.placeholders.description"
                                                    )}
                                                />
                                            </td>

                                            <td className="text-right">
                                                {rows.length > 1 ? (
                                                    <button
                                                        type="button"
                                                        className="btn kbola-iconDanger"
                                                        onClick={() => removeRow(row._rowKey)}
                                                        title={t("bulkAddGamesModal.removeRow")}
                                                    >
                                                        <i className="fas fa-trash" />
                                                    </button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="kbola-hint mt-3">
                                {t("fixtures.importModal.previewHint", {
                                    count: rows.length,
                                })}
                            </div>
                        </div>

                        <div className="modal-footer kbola-modalFooter">
                            <button
                                type="button"
                                className="btn btn-default kbola-secondaryBtn"
                                data-dismiss="modal"
                                disabled={saving}
                            >
                                {t("fixtures.importModal.close")}
                            </button>

                            <button
                                type="submit"
                                className="btn btn-kbola kbola-primaryBtn"
                                disabled={saving || parsing || !rows.length}
                            >
                                {saving
                                    ? t("fixtures.importModal.buttons.importing")
                                    : t("fixtures.importModal.buttons.importGames")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style>{`
                ${scopeSel} {
                  font-family: 'Poppins', sans-serif;
                }

                ${scopeSel} i.fas,
                ${scopeSel} i.far,
                ${scopeSel} i.fal,
                ${scopeSel} i.fa,
                ${scopeSel} i[class^="fa-"],
                ${scopeSel} i[class*=" fa-"] {
                  font-family: "Font Awesome 5 Free" !important;
                  font-weight: 900;
                }

                ${scopeSel} i.fab {
                  font-family: "Font Awesome 5 Brands" !important;
                  font-weight: 400;
                }

                ${scopeSel} .modal-dialog {
                  width: calc(100vw - 2rem);
                  max-width: 1600px;
                  max-height: calc(100% - 1.75rem * 2);
                }

                ${scopeSel} .kbola-modalContent {
                  border-radius: 18px;
                  overflow: hidden;
                  border: 1px solid rgba(0,0,0,0.08);
                  box-shadow: 0 18px 44px rgba(0,0,0,0.18);
                }

                ${scopeSel} .kbola-modalHeader {
                  padding: 14px 16px;
                  background: ${colors.primary};
                  border-bottom: 1px solid rgba(0,0,0,0.06);
                }

                ${scopeSel} .kbola-modalTitle {
                  font-size: 16px;
                  font-weight: 900;
                  color: ${colors.textOnPrimary};
                }

                ${scopeSel} .kbola-closeBtn {
                  color: ${colors.textOnPrimary};
                  opacity: 1;
                }

                ${scopeSel} .kbola-modalBody {
                  background: #fff;
                  max-height: calc(100vh - 210px);
                  overflow-y: auto;
                }

                ${scopeSel} .kbola-modalFooter {
                  padding: 12px 14px;
                  border-top: 1px solid rgba(0,0,0,0.06);
                  background: rgba(0,0,0,0.01);
                }

                ${scopeSel} .kbola-pill {
                  display: inline-flex;
                  align-items: center;
                  padding: 6px 10px;
                  border-radius: 999px;
                  font-weight: 900;
                  font-size: 12px;
                  border: 1px solid rgba(0,0,0,0.10);
                }

                ${scopeSel} .kbola-pillBrand {
                  background: ${colors.textOnPrimary};
                  color: ${colors.primary};
                }

                ${scopeSel} .kbola-pillMuted {
                  background: rgba(0,0,0,0.03);
                  color: rgba(20,21,26,0.72);
                  border-color: rgba(0,0,0,0.08);
                  font-weight: 800;
                }

                ${scopeSel} .kbola-uploadCard {
                  border-radius: 14px;
                  border: 1px solid rgba(0,0,0,0.08);
                  background: rgba(0,0,0,0.015);
                  padding: 12px;
                }

                ${scopeSel} .kbola-uploadTitle {
                  font-size: 13px;
                  font-weight: 900;
                  color: #14151a;
                }

                ${scopeSel} .kbola-uploadHint {
                  font-size: 12px;
                  font-weight: 700;
                  color: rgba(20,21,26,0.62);
                }

                ${scopeSel} .kbola-uploadTop {
                  margin-bottom: 10px;
                }

                ${scopeSel} .kbola-fileNative {
                  position: absolute;
                  width: 1px;
                  height: 1px;
                  padding: 0;
                  margin: -1px;
                  overflow: hidden;
                  clip: rect(0, 0, 0, 0);
                  white-space: nowrap;
                  border: 0;
                }

                ${scopeSel} .kbola-fileShell {
                  display: flex;
                  align-items: center;
                  gap: 10px;
                  border-radius: 14px;
                  border: 1.5px dashed ${colors.primary};
                  background: rgba(255,255,255,0.85);
                  padding: 10px;
                }

                ${scopeSel} .kbola-fileBtn {
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                  padding: 10px 12px;
                  border-radius: 12px;
                  background: ${colors.primary};
                  color: ${colors.textOnPrimary};
                  font-weight: 900;
                  cursor: pointer;
                  white-space: nowrap;
                  border: 1px solid rgba(20,21,26,0.15);
                }

                ${scopeSel} .kbola-fileMeta {
                  flex: 1;
                  min-width: 0;
                }

                ${scopeSel} .kbola-fileName {
                  display: inline-block;
                  max-width: 100%;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  font-weight: 800;
                  color: ${colors.textOnPrimary};
                  background: ${colors.primary};
                  border: 1px solid rgba(var(--kbs-primary-rgb, 0,173,255), 0.35);
                  padding: 6px 10px;
                  border-radius: 999px;
                  font-size: 12px;
                }

                ${scopeSel} .kbola-filePlaceholder {
                  color: ${colors.primary};
                  font-weight: 700;
                  font-size: 12px;
                }

                ${scopeSel} .kbola-fileSubhint {
                  margin-top: 4px;
                  font-size: 11px;
                  color: ${colors.secondary};
                  font-weight: 700;
                }

                ${scopeSel} .kbola-fileClear {
                  width: 38px;
                  height: 38px;
                  border-radius: 12px;
                  border: 1px solid rgba(20,21,26,0.10);
                  background: ${colors.primary};
                  color: ${colors.textOnPrimary};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  cursor: pointer;
                }

                ${scopeSel} .kbola-alertDanger {
                  border-radius: 14px;
                  border: 1px solid rgba(220,53,69,0.18);
                  background: rgba(220,53,69,0.06);
                  padding: 12px 14px;
                  margin-bottom: 14px;
                }

                ${scopeSel} .kbola-alertTitle {
                  font-weight: 900;
                  color: rgba(220,53,69,0.95);
                  margin-bottom: 6px;
                }

                ${scopeSel} .kbola-alertList {
                  margin: 0;
                  padding-left: 18px;
                  color: rgba(20,21,26,0.85);
                  font-weight: 700;
                  font-size: 13px;
                }

                ${scopeSel} .kbola-tableWrap {
                  position: relative;
                  border-radius: 16px;
                  overflow-x: auto;
                  overflow-y: visible;
                  min-height: 420px;
                  border: 1px solid rgba(0,0,0,0.08);
                  box-shadow: 0 10px 26px rgba(0,0,0,0.06);
                  -webkit-overflow-scrolling: touch;
                }

                ${scopeSel} .kbola-table {
                  min-width: 1230px;
                }

                ${scopeSel} .kbola-rowNumberHeader {
                  width: 72px;
                  min-width: 72px;
                  text-align: center;
                }

                ${scopeSel} .kbola-rowNumberCell {
                  width: 72px;
                  min-width: 72px;
                  text-align: center;
                }

                ${scopeSel} .kbola-rowNumberBadge {
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 34px;
                  height: 34px;
                  padding: 0 10px;
                  border-radius: 999px;
                  background: rgba(20,21,26,0.06);
                  color: rgba(20,21,26,0.78);
                  font-weight: 900;
                  font-size: 13px;
                  border: 1px solid rgba(20,21,26,0.08);
                }

                ${scopeSel} .kbola-thead th {
                  background: rgba(0,0,0,0.02);
                  border-bottom: 1px solid rgba(0,0,0,0.06);
                  color: rgba(20,21,26,0.65);
                  font-size: 12.5px;
                  font-weight: 900;
                  text-transform: uppercase;
                  letter-spacing: 0.02em;
                  padding: 12px 14px;
                  white-space: nowrap;
                }

                ${scopeSel} .kbola-tbody td {
                  border-top: 1px solid rgba(0,0,0,0.06);
                  padding: 10px 12px;
                  vertical-align: middle;
                  background: #fff;
                }

                ${scopeSel} .kbola-tbody tr:hover td {
                  background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.08);
                }

                ${scopeSel} .kbola-control {
                  border-radius: 12px;
                  border: 1px solid rgba(0,0,0,0.10);
                  font-weight: 800;
                  font-size: 14px;
                  padding: 10px 12px;
                  height: 48px;
                  box-shadow: none;
                }

                ${scopeSel} select.kbola-control {
                  min-width: 150px;
                }

                ${scopeSel} input[type="datetime-local"].kbola-control {
                  min-width: 170px;
                }

                ${scopeSel} .kbola-matchdayControl {
                  min-width: 130px;
                }

                ${scopeSel} .kbola-descriptionControl {
                  min-width: 260px;
                }

                ${scopeSel} .kbola-control:focus {
                  outline: none;
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.75);
                }

                ${scopeSel} .kbola-primaryBtn {
                  border-radius: 12px;
                  font-weight: 900;
                  padding: 10px 14px;
                  background: ${colors.primary};
                  border: 1px solid rgba(0,0,0,0.10);
                  color: ${colors.textOnPrimary};
                }

                ${scopeSel} .kbola-secondaryBtn {
                  border-radius: 12px;
                  font-weight: 900;
                  padding: 10px 14px;
                }

                ${scopeSel} .kbola-iconDanger {
                  width: 42px;
                  height: 42px;
                  border-radius: 12px;
                  border: 1px solid rgba(220,53,69,0.18);
                  background: rgba(220,53,69,0.06);
                  color: rgba(220,53,69,0.95);
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  padding: 0;
                }

                ${scopeSel} .kbola-iconDanger:hover {
                  background: rgba(220,53,69,0.10);
                }

                ${scopeSel} .kbola-hint {
                  font-size: 12px;
                  font-weight: 800;
                  color: rgba(20,21,26,0.55);
                }

                @media (max-width: 576px) {
                  ${scopeSel} .modal-dialog {
                    width: calc(100vw - 1rem);
                    margin: 0.5rem auto;
                  }

                  ${scopeSel} .kbola-tableWrap {
                    min-height: 360px;
                  }

                  ${scopeSel} .kbola-thead th {
                    padding: 10px 10px;
                  }

                  ${scopeSel} .kbola-tbody td {
                    padding: 10px 10px;
                  }
                }
            `}</style>
        </div>
    );
}
